import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Hint } from '@/components/ui/hint';
import { cn } from '@/lib/utils';
import { INVOICES_QUERY_KEY, SYNC_STATUS_QUERY_KEY, useNextSync } from './api';

/** How long "syncing" shows after a fire before we refresh the data and schedule (DB write window). */
const SYNCING_DURATION_MS = 10_000;

/** Largest two units of the remaining time: "1h 05m", "12m 30s", or "45s". Clamped at zero. */
function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  if (minutes > 0) return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
  return `${seconds}s`;
}

/**
 * A clock indicator for the next background sync: the live countdown is always shown inline beside a
 * clock icon, and a device-aware {@link Hint} explains what it is — a hover tooltip with a mouse, a tap
 * popover on touch. When a fire is due it shows "syncing" for SYNCING_DURATION_MS, then refreshes the
 * visible invoices and the schedule. The API hands us the next two fire times, so the moment "syncing"
 * ends the countdown rolls straight to the following one (held locally) with no gap, while the refetch
 * brings the next pair.
 */
export function NextSyncIndicator() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data } = useNextSync();
  const times = data?.next;
  const firstAt = times?.[0];

  const [now, setNow] = useState(() => Date.now());
  const [syncing, setSyncing] = useState(false);

  // The countdown is always on screen, so tick every second.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // At the fire, show "syncing"; SYNCING_DURATION_MS later, clear it and refresh the invoices and the
  // schedule together. The refetched schedule re-runs this effect and re-arms the timers for the next.
  useEffect(() => {
    if (!firstAt) return;
    const dueMs = new Date(firstAt).getTime() - Date.now();
    setSyncing(dueMs <= 0 && dueMs > -SYNCING_DURATION_MS);
    const dueTimer = window.setTimeout(() => setSyncing(true), Math.max(0, dueMs));
    const refreshTimer = window.setTimeout(
      () => {
        setSyncing(false);
        void queryClient.invalidateQueries({ queryKey: INVOICES_QUERY_KEY });
        void queryClient.invalidateQueries({ queryKey: SYNC_STATUS_QUERY_KEY });
      },
      Math.max(0, dueMs + SYNCING_DURATION_MS),
    );
    return () => {
      window.clearTimeout(dueTimer);
      window.clearTimeout(refreshTimer);
    };
  }, [firstAt, queryClient]);

  if (!times || times.length === 0) return null;

  // The soonest time still in the future; once one passes we fall through to the following one (held
  // from the API) so the countdown rolls over with no gap. Fallback keeps the icon during the refetch.
  const upcoming = times.map((iso) => new Date(iso).getTime());
  const targetMs = upcoming.find((ms) => ms > now) ?? upcoming[upcoming.length - 1];
  if (targetMs == null) return null;

  const countdown = formatCountdown(targetMs - now);
  const valueText = syncing ? t('invoices.next_sync.syncing') : countdown;
  // The value is shown inline, so `aria-label` carries the full phrase (value + meaning) for assistive
  // tech, while the Hint explains the widget to sighted hover/tap users.
  const ariaLabel = syncing
    ? t('invoices.next_sync.syncing')
    : t('invoices.next_sync.tooltip', { countdown });

  return (
    <Hint content={t('invoices.next_sync.hint')}>
      <span
        role="img"
        tabIndex={0}
        aria-label={ariaLabel}
        className={cn(
          'inline-flex h-9 items-center gap-1.5 rounded-md px-2 text-xs tabular-nums text-muted-foreground',
          'outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
          syncing && 'text-foreground',
        )}
      >
        <Clock className={cn('size-4', syncing && 'animate-pulse')} />
        <span aria-hidden>{valueText}</span>
      </span>
    </Hint>
  );
}
